import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Image, Plus, Trash2, Edit2, Copy, CheckCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaManager = ({ token }) => {
  const [media, setMedia] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);

  useEffect(() => {
    loadMedia();
    loadDestinations();
  }, [selectedCategory]);

  const loadDestinations = async () => {
    try {
      const response = await axios.get(`${API}/destinations?active_only=false`);
      setDestinations(response.data.destinations || []);
    } catch (error) {
      console.error('Error loading destinations:', error);
    }
  };

  const loadMedia = async () => {
    setIsLoading(true);
    try {
      const categoryParam = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/admin/media${categoryParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(response.data.media || []);
    } catch (error) {
      console.error('Error loading media:', error);
      toast.error('Fehler beim Laden der Medien');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (mediaItem) => {
    setEditingMedia(mediaItem);
    setShowEditModal(true);
  };

  const handleUpdate = async (updatedMedia) => {
    try {
      await axios.put(
        `${API}/admin/media/${updatedMedia.id}`,
        updatedMedia,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Medium aktualisiert');
      setShowEditModal(false);
      setEditingMedia(null);
      await loadMedia();
    } catch (error) {
      console.error('Error updating media:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Medium wirklich löschen?')) return;

    try {
      await axios.delete(`${API}/admin/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Medium gelöscht');
      await loadMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL kopiert!');
  };

  const handleFileUpload = async (files, destinationId = null) => {
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    const uploadResults = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = `upload_${Date.now()}_${i}`;
      
      try {
        // Update progress
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { name: file.name, progress: 0, status: 'uploading' }
        }));
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', selectedCategory === 'all' ? 'other' : selectedCategory);
        if (destinationId) {
          formData.append('destination_id', destinationId);
        }
        
        const response = await axios.post(
          `${API}/admin/media/upload`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({
                ...prev,
                [fileId]: { ...prev[fileId], progress: percentCompleted }
              }));
            }
          }
        );
        
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { ...prev[fileId], status: 'success', progress: 100 }
        }));
        
        uploadResults.push(response.data);
        toast.success(`${file.name} hochgeladen!`);
        
      } catch (error) {
        console.error('Upload error:', error);
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { ...prev[fileId], status: 'error', progress: 0 }
        }));
        toast.error(`Fehler bei ${file.name}: ${error.response?.data?.detail || error.message}`);
      }
    }
    
    // Clear progress after 3 seconds
    setTimeout(() => {
      setUploadProgress({});
      setIsUploading(false);
    }, 3000);
    
    // Reload media list
    await loadMedia();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const categories = [
    { value: 'all', label: 'Alle' },
    { value: 'destination', label: 'Destinationen' },
    { value: 'hero', label: 'Hero Bilder' },
    { value: 'logo', label: 'Logos' },
    { value: 'other', label: 'Sonstiges' }
  ];

  if (isLoading) {
    return (
      <div className="media-manager-loading">
        <Image size={48} />
        <p>Lade Medien...</p>
      </div>
    );
  }

  return (
    <div className="media-manager">
      <div className="media-manager-header">
        <div>
          <h2>Media Manager</h2>
          <p>Verwalte Bilder und Media-URLs</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          <Plus size={20} />
          Neues Medium hinzufügen
        </button>
      </div>

      <div className="media-filters">
        {categories.map(cat => (
          <button
            key={cat.value}
            className={`filter-btn ${selectedCategory === cat.value ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.value)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Upload Zone */}
      <div 
        className="upload-zone"
        style={{
          border: '2px dashed #d1d5db',
          borderRadius: '12px',
          padding: '40px',
          marginBottom: '30px',
          backgroundColor: isUploading ? '#f9fafb' : 'white'
        }}
      >
        <div 
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          style={{
            textAlign: 'center',
            cursor: 'pointer',
            paddingBottom: selectedCategory === 'destination' ? '20px' : '0'
          }}
          onClick={() => document.getElementById('file-input').click()}
        >
          <Image size={48} color="#9ca3af" style={{ margin: '0 auto 16px' }} />
          <h3 style={{ marginBottom: '8px', color: '#374151' }}>
            Bilder hochladen zu Cloudinary
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            Drag & Drop oder hier klicken um Dateien auszuwählen
          </p>
          <p style={{ fontSize: '14px', color: '#9ca3af' }}>
            Unterstützt: JPG, PNG, WebP | Max 10 MB pro Bild
          </p>
          <input
            id="file-input"
            type="file"
            multiple
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => handleFileUpload(Array.from(e.target.files), selectedDestination)}
          />
        </div>

        {/* Destination Selector (nur anzeigen wenn Destinationen-Kategorie gewählt) */}
        {selectedCategory === 'destination' && destinations.length > 0 && (
          <div style={{ 
            marginTop: '20px', 
            paddingTop: '20px', 
            borderTop: '1px solid #e5e7eb',
            textAlign: 'left'
          }}>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#374151',
              marginBottom: '8px'
            }}>
              📍 Destination zuweisen (optional):
            </label>
            <select
              value={selectedDestination || ''}
              onChange={(e) => setSelectedDestination(e.target.value || null)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">Keine Destination</option>
              {destinations
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(dest => (
                  <option key={dest.id} value={dest.id}>
                    {dest.name} ({dest.continent || 'N/A'})
                  </option>
                ))}
            </select>
            <small style={{ display: 'block', marginTop: '8px', color: '#6b7280' }}>
              {selectedDestination 
                ? `Bilder werden automatisch dieser Destination zugewiesen` 
                : 'Wählen Sie eine Destination für automatische Zuweisung'}
            </small>
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '20px', 
          backgroundColor: '#f9fafb', 
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <h4 style={{ marginBottom: '16px', color: '#374151' }}>Upload-Fortschritt</h4>
          {Object.entries(uploadProgress).map(([id, item]) => (
            <div key={id} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '14px', color: '#374151' }}>{item.name}</span>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  {item.status === 'success' ? '✅' : item.status === 'error' ? '❌' : `${item.progress}%`}
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '6px', 
                backgroundColor: '#e5e7eb', 
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${item.progress}%`, 
                  height: '100%', 
                  backgroundColor: item.status === 'success' ? '#10b981' : item.status === 'error' ? '#ef4444' : '#c084fc',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {media.length === 0 ? (
        <div className="media-empty">
          <Image size={64} color="#9CA3AF" />
          <h3>Keine Medien vorhanden</h3>
          <p>Füge dein erstes Medium hinzu</p>
        </div>
      ) : (
        <div className="media-grid">
          {media.map((item) => (
            <div key={item.id} className="media-card">
              <div className="media-preview">
                {item.type === 'image' ? (
                  <img 
                    src={item.url} 
                    alt={item.name} 
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;background:#f3f4f6;color:#9ca3af;font-size:14px;">Bild nicht verfügbar</div>';
                    }} 
                  />
                ) : (
                  <div className="video-placeholder">
                    <ExternalLink size={32} />
                  </div>
                )}
              </div>
              
              <div className="media-info">
                <h4>{item.name}</h4>
                {item.destination_name && (
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#c084fc', 
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    📍 {item.destination_name}
                  </p>
                )}
                {item.description && (
                  <p style={{ fontSize: '13px', color: '#6b7280' }}>
                    {item.description}
                  </p>
                )}
                <div className="media-meta">
                  <span className="media-category">{item.category}</span>
                  <span className="media-type">{item.type}</span>
                </div>
              </div>

              <div className="media-actions">
                {item.editable && (
                  <button
                    className="btn-icon"
                    onClick={() => handleEdit(item)}
                    title="Bearbeiten"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
                <button
                  className="btn-icon"
                  onClick={() => copyUrl(item.url)}
                  title="URL kopieren"
                >
                  <Copy size={16} />
                </button>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-icon"
                  title="Öffnen"
                >
                  <ExternalLink size={16} />
                </a>
                {!item.editable && (
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => handleDelete(item.id)}
                    title="Löschen"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddMediaModal
          onClose={() => setShowAddModal(false)}
          onCreate={async (newMedia) => {
            try {
              await axios.post(
                `${API}/admin/media`,
                newMedia,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success('Medium hinzugefügt');
              setShowAddModal(false);
              await loadMedia();
            } catch (error) {
              console.error('Error creating media:', error);
              toast.error('Fehler beim Hinzufügen');
            }
          }}
        />
      )}
      
      {showEditModal && editingMedia && (
        <EditMediaModal
          media={editingMedia}
          token={token}
          onClose={() => {
            setShowEditModal(false);
            setEditingMedia(null);
          }}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
};

const EditMediaModal = ({ media, onClose, onUpdate, token }) => {
  const [editedMedia, setEditedMedia] = useState({
    id: media.id,
    url: media.url,
    destination_id: media.destination_id,
    field_name: media.field_name,
    gallery_index: media.gallery_index
  });
  const [previewError, setPreviewError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', media.category || 'other');
      if (media.destination_id) {
        formData.append('destination_id', media.destination_id);
      }
      
      const response = await axios.post(
        `${API}/admin/media/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      // Update URL with new Cloudinary URL
      setEditedMedia({ ...editedMedia, url: response.data.url });
      setPreviewError(false);
      toast.success('Bild hochgeladen!');
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Upload fehlgeschlagen: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Get full URL for preview (handle relative URLs)
  const getPreviewUrl = (url) => {
    if (!url) return '';
    // If it's a relative URL, prepend the backend URL
    if (url.startsWith('/')) {
      return url; // Browser will resolve it relative to current domain
    }
    return url;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Bild bearbeiten</h3>
        <p className="modal-subtitle">{media.name}</p>
        
        <form onSubmit={(e) => { e.preventDefault(); onUpdate(editedMedia); }}>
          <div className="form-group">
            <label>Aktuelle URL</label>
            <input
              type="text"
              value={media.url}
              disabled
              style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
            />
          </div>

          <div className="form-group">
            <label>Neue URL oder Datei hochladen</label>
            <small style={{ display: 'block', color: '#6b7280', marginBottom: '8px' }}>
              Option 1: URL eingeben (z.B. https://...) ODER Option 2: Neues Bild hochladen ⬇️
            </small>
            <input
              type="text"
              value={editedMedia.url}
              onChange={(e) => {
                setEditedMedia({...editedMedia, url: e.target.value});
                setPreviewError(false);
              }}
              placeholder="https://... oder /destination-images/..."
              required
              disabled={isUploading}
            />
            
            {/* Upload Button */}
            <div style={{ marginTop: '12px' }}>
              <input
                type="file"
                id="edit-file-input"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleFileUpload(e.target.files[0]);
                  }
                }}
              />
              <button
                type="button"
                onClick={() => document.getElementById('edit-file-input').click()}
                disabled={isUploading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isUploading ? '#d1d5db' : '#c084fc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                {isUploading ? (
                  <>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Hochladen... {uploadProgress}%
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Neues Bild hochladen
                  </>
                )}
              </button>
            </div>
            
            {/* Progress Bar */}
            {isUploading && (
              <div style={{ 
                marginTop: '12px',
                width: '100%', 
                height: '6px', 
                backgroundColor: '#e5e7eb', 
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${uploadProgress}%`, 
                  height: '100%', 
                  backgroundColor: '#c084fc',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Vorschau</label>
            {editedMedia.url && (
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                {!previewError ? (
                  <img 
                    src={getPreviewUrl(editedMedia.url)}
                    alt="Preview" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '300px', 
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                    onError={(e) => {
                      console.error('Image load error for:', editedMedia.url);
                      setPreviewError(true);
                    }}
                    onLoad={() => {
                      console.log('Image loaded successfully:', editedMedia.url);
                      setPreviewError(false);
                    }}
                  />
                ) : (
                  <div style={{ 
                    padding: '40px', 
                    backgroundColor: '#f3f4f6', 
                    borderRadius: '8px',
                    color: '#6b7280'
                  }}>
                    <Image size={48} style={{ margin: '0 auto 10px' }} />
                    <p>❌ Bild konnte nicht geladen werden</p>
                    <small>Überprüfen Sie die URL oder versuchen Sie eine andere</small>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary" disabled={previewError}>
              <CheckCircle size={16} />
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddMediaModal = ({ onClose, onCreate }) => {
  const [newMedia, setNewMedia] = useState({
    name: '',
    url: '',
    type: 'image',
    category: 'other',
    description: ''
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>Neues Medium hinzufügen</h3>
        
        <form onSubmit={(e) => { e.preventDefault(); onCreate(newMedia); }}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={newMedia.name}
              onChange={(e) => setNewMedia({...newMedia, name: e.target.value})}
              placeholder="z.B. Hero Background"
              required
            />
          </div>

          <div className="form-group">
            <label>URL</label>
            <input
              type="url"
              value={newMedia.url}
              onChange={(e) => setNewMedia({...newMedia, url: e.target.value})}
              placeholder="https://..."
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Typ</label>
              <select
                value={newMedia.type}
                onChange={(e) => setNewMedia({...newMedia, type: e.target.value})}
              >
                <option value="image">Bild</option>
                <option value="video">Video</option>
              </select>
            </div>

            <div className="form-group">
              <label>Kategorie</label>
              <select
                value={newMedia.category}
                onChange={(e) => setNewMedia({...newMedia, category: e.target.value})}
              >
                <option value="destination">Destination</option>
                <option value="hero">Hero</option>
                <option value="logo">Logo</option>
                <option value="other">Sonstiges</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Beschreibung (optional)</label>
            <textarea
              value={newMedia.description}
              onChange={(e) => setNewMedia({...newMedia, description: e.target.value})}
              rows={2}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Abbrechen
            </button>
            <button type="submit" className="btn-primary">
              <Plus size={16} />
              Hinzufügen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MediaManager;

